// src/components/tasks/TaskStatisticsDialog.tsx
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import type { ExtendedTask } from "@/types/task"
import { useMemo } from "react"

// === ДИЗАЙН-СЛОЙ ===
import { statisticsTheme } from "./ui/TaskStatisticsDialogUI"

const employees = [
  { id: 1, name: "Андреев Алексей Евгеньевич" },
  { id: 2, name: "Багаутдинов Ахмет Айратович" },
  { id: 3, name: "Барладян Ирина Алексеевна" },
  { id: 4, name: "Павлик Кристина Валентиновна" },
  { id: 5, name: "Султанова Диана Рашитовна" },
  { id: 6, name: "Казарин Сергей Константинович" },
  { id: 7, name: "Степанушкин Сергей Вадимович" },
]

interface TaskStatisticsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tasks: ExtendedTask[]
}

export default function TaskStatisticsDialog({
  open,
  onOpenChange,
  tasks,
}: TaskStatisticsDialogProps) {
  const stats = useMemo(() => {
    const result: Record<number, any> = {}

    employees.forEach((emp) => {
      const empId = emp.id

      const assignedTasks = tasks.filter((t) => Number(t.assigneeId) === empId)

      const isCompleted = (t: ExtendedTask) => {
        return (
          t.approvalStatus === "approved" ||
          t.status === "done" ||
          t.approvalStatus === "done" ||
          (t.approvedByCreator === true && t.approvalStatus === "in_review")
        )
      }

      const completed = assignedTasks.filter(isCompleted).length
      const activeAssigned = assignedTasks.filter((t) => !isCompleted(t))

      const activeCoAssigned = tasks.filter(
        (t) => t.coAssigneeIds?.includes(empId) && !isCompleted(t)
      ).length

      const activeObserver = tasks.filter(
        (t) => t.observerIds?.includes(empId) && !isCompleted(t)
      ).length

      const byStatus = {
        new: activeAssigned.filter((t) => t.approvalStatus === "new").length,
        accepted: activeAssigned.filter((t) => t.approvalStatus === "accepted").length,
        inReview: activeAssigned.filter((t) => t.approvalStatus === "in_review").length,
        rework: activeAssigned.filter((t) => t.approvalStatus === "rework").length,
      }

      result[empId] = {
        name: emp.name,
        assigned: activeAssigned.length,
        coAssigned: activeCoAssigned,
        observer: activeObserver,
        completed,
        byStatus,
        totalAssigned: assignedTasks.length,
      }
    })

    return result
  }, [tasks])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={statisticsTheme.dialogContent}>
        <DialogHeader className={statisticsTheme.header}>
          <DialogTitle className={statisticsTheme.title}>
            📊 Статистика задач по сотрудникам
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 p-8 overflow-y-auto custom-scroll">
          <div className="grid grid-cols-1 gap-8 pb-8">
            {employees.map((emp) => {
              const s = stats[emp.id] || {
                assigned: 0,
                coAssigned: 0,
                observer: 0,
                completed: 0,
                byStatus: {},
                totalAssigned: 0,
              }

              return (
                <div key={emp.id} className={statisticsTheme.employeeCard}>
                  <div className="flex justify-between items-start mb-6">
                    <h3 className="text-2xl font-semibold text-white">{emp.name}</h3>
                    <div className="text-right">
                      <p className="text-xs text-purple-400">Всего в исполнении</p>
                      <p className="text-3xl font-bold text-purple-100">{s.totalAssigned}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-5 mb-8">
                    <div className={statisticsTheme.statBlock("purple")}>
                      <p className={statisticsTheme.statLabel + " text-purple-300"}>В исполнении</p>
                      <p className={statisticsTheme.statValue + " text-purple-100"}>{s.assigned}</p>
                    </div>
                    <div className={statisticsTheme.statBlock("pink")}>
                      <p className={statisticsTheme.statLabel + " text-pink-300"}>На соисполнении</p>
                      <p className={statisticsTheme.statValue + " text-pink-100"}>{s.coAssigned}</p>
                    </div>
                    <div className={statisticsTheme.statBlock("amber")}>
                      <p className={statisticsTheme.statLabel + " text-amber-300"}>Наблюдатель</p>
                      <p className={statisticsTheme.statValue + " text-amber-100"}>{s.observer}</p>
                    </div>
                    <div className={statisticsTheme.statBlock("emerald")}>
                      <p className={statisticsTheme.statLabel + " text-emerald-300"}>Завершённые</p>
                      <p className={statisticsTheme.statValue + " text-emerald-100"}>{s.completed}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-purple-300 mb-4">Статусы задач в исполнении:</p>
                    <div className="flex flex-wrap gap-3">
                      <Badge className={statisticsTheme.badgeNew}>Новое: {s.byStatus.new}</Badge>
                      <Badge className={statisticsTheme.badgeAccepted}>Принято: {s.byStatus.accepted}</Badge>
                      <Badge className={statisticsTheme.badgeInReview}>На согласовании: {s.byStatus.inReview}</Badge>
                      <Badge className={statisticsTheme.badgeRework}>На доработке: {s.byStatus.rework}</Badge>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}